import * as React from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Pencil } from "lucide-react";

import DetailDrawer from "@/components/DetailDrawer";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Detail drawer for a channel: description, meta (site scope, created by),
 * and archive/unarchive actions (gated by comms.manage).
 */
export default function ChannelDetailDrawer({ open, onOpenChange, companyId, channel, canManage, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (channel) {
      setName(channel.name || "");
      setDescription(channel.description || "");
      setEditing(false);
    }
    // Reset only when the channel *identity* changes, not on every field edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.id]);

  if (!channel) return null;

  async function saveEdit() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/companies/${companyId}/comms/channels/${channel.id}`,
        { name: name.trim(), description: description.trim() },
      );
      toast.success(`Saved #${data.slug}`);
      onChanged && onChanged(data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/companies/${companyId}/comms/channels/${channel.id}`,
        { archived: !channel.archived },
      );
      toast.success(data.archived ? "Archived" : "Unarchived");
      onChanged && onChanged(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`#${channel.slug}`}
      subtitle={channel.archived ? "Archived channel" : "Channel"}
      testid="comms-channel-detail"
    >
      <div className="px-5 py-4 space-y-5">
        {/* Description block */}
        <div>
          <div className="flex items-center justify-between">
            <div className="micro-label">Description</div>
            {canManage && !editing && (
              <button type="button" className="btn-ghost h-6 text-[11px]"
                      onClick={() => setEditing(true)}
                      data-testid="comms-channel-edit-trigger">
                <Pencil size={11} /> Edit
              </button>
            )}
          </div>
          {editing ? (
            <div className="mt-1 space-y-2">
              <div>
                <label className="micro-label">Name</label>
                <input
                  className="field-input mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={48}
                  data-testid="comms-channel-edit-name"
                />
              </div>
              <div>
                <label className="micro-label">Description</label>
                <textarea
                  className="field-input py-2 min-h-[80px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={280}
                  data-testid="comms-channel-edit-desc"
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={saveEdit} disabled={busy || !name.trim()} className="btn-violet"
                        data-testid="comms-channel-edit-save">Save</button>
                <button type="button" onClick={() => setEditing(false)} className="btn-outline"
                        data-testid="comms-channel-edit-cancel">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="mt-1 text-[13px] text-brand-ink">
              {channel.description || <span className="text-brand-faint">No description.</span>}
            </div>
          )}
        </div>

        {/* Meta */}
        <div>
          <div className="micro-label">Meta</div>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
            <dt className="text-brand-muted">Kind</dt>
            <dd className="text-brand-ink">Channel</dd>
            <dt className="text-brand-muted">Scope</dt>
            <dd className="text-brand-ink">{channel.site_id ? "Site-scoped" : "Company-wide"}</dd>
            <dt className="text-brand-muted">Created</dt>
            <dd className="mono-num text-brand-ink">{channel.created_at?.slice(0, 10) || "—"}</dd>
            <dt className="text-brand-muted">Status</dt>
            <dd className="text-brand-ink">{channel.archived ? "Archived" : "Active"}</dd>
          </dl>
        </div>

        {/* Actions */}
        {canManage && (
          <div className="pt-2 border-t border-brand-rule">
            <div className="micro-label">Actions</div>
            <div className="mt-2">
              <button
                type="button"
                onClick={toggleArchive}
                disabled={busy}
                className="btn-outline"
                data-testid="comms-channel-archive"
              >
                {channel.archived
                  ? <><ArchiveRestore size={13} /> Unarchive channel</>
                  : <><Archive size={13} /> Archive channel</>}
              </button>
              <div className="mt-2 text-[11px] text-brand-muted">
                Archived channels are hidden from the rail. Their message
                history is preserved and readable by anyone with
                <span className="mono-num"> comms.manage</span>.
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}
